import React, { useCallback, useEffect, useState } from 'react';
import { IDSAlert, IDSCheckbox } from '@inera/ids-react';
import ReviewerDecisionQueueList from '../components/reviewer/ReviewerDecisionQueueList';
import {
  executeReviewerDecision,
  fetchReviewerDecisionQueue,
} from '../services/reviewerDecisionQueueAPI';
import {
  isValidReservationTicketKey,
  normalizeReservationTicketKey,
  requiresReservationTicketKey,
  supportsDeviationLink,
} from '../components/reviewer/reviewerDecisionQueueUtils';

const ReviewerDecisionQueue = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeHandled, setIncludeHandled] = useState(false);
  const [executingDeviationId, setExecutingDeviationId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [reservationStateByDeviationId, setReservationStateByDeviationId] = useState({});

  const loadQueue = useCallback(async (showHandled) => {
    setLoading(true);
    setError('');
    try {
      const queue = await fetchReviewerDecisionQueue(showHandled);
      setRows(Array.isArray(queue) ? queue : []);
    } catch (err) {
      setError(err?.message || 'Kunde inte hämta beslutskön.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue(includeHandled);
  }, [includeHandled, loadQueue]);

  const onReservationTicketKeyChange = (deviationId, value) => {
    setReservationStateByDeviationId((prev) => ({
      ...prev,
      [deviationId]: {
        ...prev[deviationId],
        reservationTicketKey: value,
      },
    }));
  };

  const onExecute = async (row) => {
    const rowState = reservationStateByDeviationId[row.deviationId] || {};
    const reservationKey = normalizeReservationTicketKey(rowState.reservationTicketKey);

    if (reservationKey && !isValidReservationTicketKey(reservationKey)) {
      setStatusMessage({
        type: 'warning',
        text: 'Ogiltig Jira-nyckel. Använd formatet INAV-123.',
      });
      return;
    }

    if (requiresReservationTicketKey(row) && !reservationKey) {
      return;
    }

    setExecutingDeviationId(row.deviationId);
    setStatusMessage(null);

    try {
      const payload = supportsDeviationLink(row.jiraDecision)
        ? { reservationTicketKey: reservationKey || null }
        : {};

      const result = await executeReviewerDecision(row.deviationId, payload);

      if (result?.processed) {
        setStatusMessage({ type: 'success', text: 'Beslut utfört.' });
        await loadQueue(includeHandled);
      } else {
        setStatusMessage({
          type: 'warning',
          text: result?.message || 'Beslutet kunde inte utföras.',
        });
      }
    } catch (err) {
      setStatusMessage({
        type: 'error',
        text: err?.message || 'Ett fel uppstod när beslutet skulle utföras.',
      });
    } finally {
      setExecutingDeviationId(null);
    }
  };

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '1440px', margin: '2rem auto' }}>
      <h1 className="ids-heading-l">Jira beslutskö</h1>
      <p style={{ color: 'var(--ids-palette-neutral-60)', marginBottom: '1.5rem' }}>
        Kö med Jira-ärenden där granskarbeslut ska utföras.
      </p>

      <div className="ids-mb-4">
        <IDSCheckbox checked={includeHandled} onChange={() => setIncludeHandled((v) => !v)}>
          Visa hanterade
        </IDSCheckbox>
      </div>

      {statusMessage && (
        <div className="ids-mb-4">
          <IDSAlert ribbon type={statusMessage.type}>
            {statusMessage.text}
          </IDSAlert>
        </div>
      )}

      {loading && <p>Laddar beslutskö…</p>}

      {!loading && error && (
        <IDSAlert type="error">
          {error}
        </IDSAlert>
      )}

      {!loading && !error && rows.length === 0 && (
        <p>Inga rader i beslutskön.</p>
      )}

      {!loading && !error && rows.length > 0 && (
        <ReviewerDecisionQueueList
          rows={rows}
          executingDeviationId={executingDeviationId}
          reservationStateByDeviationId={reservationStateByDeviationId}
          onReservationTicketKeyChange={onReservationTicketKeyChange}
          onExecute={onExecute}
        />
      )}
    </main>
  );
};

export default ReviewerDecisionQueue;
